import { NoSearchResults, SearchForm } from "../../search/search";
import { useTranslate } from "../../providers/LocalizationProvider";
import { useState } from "react";
import { nonWhitespacePattern, normalizeString, SearchRegExp } from "../../../utils/utils";
import { useOrganisationStyles } from "./useOrganisationStyles";
import { ChevronRight } from "@mui/icons-material";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { makeStyles } from "../../providers/makeStyles";
import { OrganisationAvatar } from "../avatar/OrganisationAvatar";
import { ListItemSelectable } from "../lists/ListItemSelectable";
import { ListItemContainer } from "../lists/ListItem";
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const getFirstLetter = org => {
    const firstLetter = normalizeString(org.name.charAt(0).toUpperCase());
    return firstLetter.match(/[A-Z]/) ? firstLetter : "#";
};

const useFederatedOrganisationsStyle = makeStyles(theme => ({
    noResults: {
        padding: theme.spacing(2,0,1)
    },
}));
export const FederatedOrganisations = ({ organisation }) => {
    const [search, setSearch] = useState("");
    const classes = { ...useOrganisationStyles(), ...useFederatedOrganisationsStyle() };

    const getOrgs = (organisations) => {
        const orgs = [...organisations].sort((a, b) => {
            if (getFirstLetter(a) === "#" && getFirstLetter(b) !== "#") return 1;
            if (getFirstLetter(b) === "#" && getFirstLetter(a) !== "#") return -1;
            return normalizeString(a.name.toUpperCase()) > normalizeString(b.name.toUpperCase()) ? 1 : -1;
        });

        const words = search.match(nonWhitespacePattern);
        if (words) {
            const searchResults = [];
            searchResults["Your search results"] = words.reduce((orgs, word) => {
                const searchRegex = SearchRegExp(word);

                return orgs.filter(org => org.name.match(searchRegex));
            }, orgs);
            return searchResults;
        }

        return orgs.reduce((accumulatedOrgs, currOrg) => {
            const firstLetter = getFirstLetter(currOrg);
            const groupedOrgs = accumulatedOrgs[firstLetter] || [];
            accumulatedOrgs[firstLetter] = [...groupedOrgs, currOrg];
            return accumulatedOrgs;
        }, []);
    };

    const filteredOrganisations = Object.entries(getOrgs(organisation.federatedOrganisations));
    const maxVisibleFilteredOrganisations = 5;
    const showSearchInput = organisation.federatedOrganisations.length > maxVisibleFilteredOrganisations;

    const noResults = useTranslate("Sorry, we can't seem to find that organisation 😞");
    const clearResults = useTranslate("clear_search_to_view_all_federated_organisations",{ federatedOrganisationTerm: organisation.federatedOrganisationTerm });
    
    const findFederatedOrganisation = useTranslate("find_federated_organisation", { federatedOrganisationTerm: organisation.federatedOrganisationTerm });
    
    return (
        <>
            {showSearchInput && <SearchForm
                inHeading
                className={classes.search}
                search={search}
                placeholder={findFederatedOrganisation}
                onChange={setSearch}
            />}

            {filteredOrganisations?.[0]?.[1].length === 0 ?
                <NoSearchResults
                    className={classes.noResults}
                    noResultsText={noResults}
                    clearResultsText={clearResults}
                    onClear={() => setSearch("")}
                    restrictMaxWidth/> :
                filteredOrganisations.map(federationOrganisation =>
                    <FilteredOrganisations key={federationOrganisation[0]}
                                           organisations={federationOrganisation[1]} />
                )}
        </>
    );
};

export const FilteredOrganisations = ({ organisations }) => {
    return <section>
        <ListItemContainer>
            {organisations.map(groupedOrganisation =>
                <OrganisationRow key={groupedOrganisation.id} organisation={groupedOrganisation}/>)}
        </ListItemContainer>
    </section>;
};

export const OrganisationRow = ({ organisation }) => {
    const isMobile = useIsMobile();
    const history = useHistory();

    const handleSelect = () => {
        history.push(`/${organisation.shortName}`);
    };
    
    return <ListItemSelectable 
        title={organisation.name}
        icon={<OrganisationAvatar alt={organisation.name} src={organisation.logo} size="xl"/>}
        aside={!isMobile && <ChevronRight />}
        onSelect={handleSelect}
    />;
};
